/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React from 'react';
import { Modal, message, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { appLog } from '~src/utils/log';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useProjectId } from '~src/hooks/useProjectId';
import { Tag } from '~src/services/api/archiveSettings';
import { deleteTag } from '~src/services/api';
import { DbTenderUser } from '~src/types/db';
import { removeTenderUser } from '~src/services/api/tender';

export interface DeleteModalProps extends Partial<ModalProps> {
  onDone?: () => void;
  user: DbTenderUser | null;
}
export const DeleteTenderUserModal: React.FC<DeleteModalProps> = ({
  onCancel,
  onDone,
  user,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const projectId = useProjectId();

  const [doDeleteUser, { isLoading }] = useMutation(removeTenderUser, {
    onSuccess: async ({}) => {
      // hide modal
      // @ts-ignore
      onDone ? onDone() : onCancel && onCancel();
      queryCache.invalidateQueries('tenderUsersList');
      queryCache.invalidateQueries('tenderArchiveList');
      message.success(intl.formatMessage({ id: 'remove-user-success' }));
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'delete-failed' }));
      appLog('DeleteTenderUserModal -> removeTenderUser -> items', item);
      appLog('DeleteTenderUserModal -> removeTenderUser -> err', error);
    },
  });

  const handleSubmit = async () => {
    if (user?.id) {
      doDeleteUser({ projectId: String(projectId), userId: String(user?.id) });
    }
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'delete' })}
          icon={['far', 'trash']}
        />
      }
      okText={intl.formatMessage({ id: 'delete' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ danger: true, loading: isLoading }}
      onOk={handleSubmit}
      onCancel={onCancel}
      {...modalProps}>
      <Typography.Paragraph css={styles.text}>
        {intl.formatMessage(
          { id: 'delete-tender-user-single-text' },
          {
            type:
              (user as DbTenderUser)?.tender_user_type === 1
                ? intl.formatMessage({ id: 'admin' })
                : intl.formatMessage({ id: 'manager' }),
            name: (user as DbTenderUser)?.full_name,
          }
        )}
      </Typography.Paragraph>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  text: {
    textAlign: 'center',
    margin: 0,
    fontSize: 15,
  },
});
